import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('tabManagerStore');

// Type definitions
export interface Tab {
  id: string;
  type: string;
  name: string;
  content?: string;
  isModified?: boolean; // Flag to indicate if tab has unsaved changes
  filePath?: string; // Path to saved file for subsequent saves
  isNew?: boolean; // Flag to indicate if this is a new file (true) or opened from browser (false)
}

export interface FileHistoryEntry {
  id: string;
  type: string;
  name: string;
  lastOpened: string; // ISO string timestamp
  path?: string; // Optional file path for saved files
}

export interface TabManagerStore {
  tabs: Tab[];
  activeTabId: string | null;
  fileHistory: FileHistoryEntry[]; // Array of recently opened files
  openTab: (id: string, type: string, name?: string, content?: string, path?: string, isNew?: boolean) => void;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  clearActiveTab: () => void;
  updateTabName: (id: string, newName: string) => void;
  updateTabContent: (id: string, content: string) => void;
  updateTabModified: (id: string, isModified: boolean) => void; // New method to update modified flag
  updateTabPath: (id: string, filePath: string) => void; // New method to update file path
  updateTabIsNew: (id: string, isNew: boolean) => void; // New method to update isNew flag
  migrateTabId: (oldId: string, newId: string, newName: string, filePath: string) => void; // Migrate tab to new ID
  reorderTabs: (sourceIndex: number, destinationIndex: number) => void;
  handleAddTab: (type: string, id?: string | null, name?: string | null, path?: string) => void;
  addToFileHistory: (id: string, type: string, name: string, path?: string) => void;
  removeFromFileHistory: (id: string) => void;
  clearFileHistory: () => void;
  getRecentFiles: (limit?: number) => FileHistoryEntry[];
  clearStore: () => void;
}

const useTabManagerStore = create<TabManagerStore>()(
  persist(
    (set, get) => ({
      tabs: [], // Array of tab objects with id and type
      activeTabId: null,
      fileHistory: [], // Array of recently opened files

      openTab: (id: string, type: string, name: string = `New ${type}`, content: string = '', path?: string, isNew: boolean = false) => {
        const state = get();
        
        // Check if tab already exists
        const existingTab = state.tabs.find((tab) => tab.id === id);
        if (existingTab) {
          // Update existing tab with new information (name, path)
          set((state) => ({
            tabs: state.tabs.map((tab) => 
              tab.id === id ? { ...tab, name, filePath: path || tab.filePath, isNew: isNew !== undefined ? isNew : tab.isNew } : tab
            ),
            activeTabId: id,
          }));
          // Update file history for existing tab
          get().addToFileHistory(id, type, name, path);
          logger.debug('Switched to and updated existing tab:', {
            id,
            type,
            name,
            path,
            isNew,
            timestamp: new Date().toISOString(),
          });
          return;
        }

        // Add new tab
        set((state) => ({
          tabs: [...state.tabs, { id, type, name, content, isModified: false, filePath: path, isNew }],
          activeTabId: id,
        }));
        
        // Add to file history
        get().addToFileHistory(id, type, name, path);
        
        logger.debug('Created new tab via openTab:', {
          id,
          type,
          name,
          isNew,
          timestamp: new Date().toISOString(),
        });
      },

      closeTab: (id: string) => {
        const state = get();
        const newTabs = state.tabs.filter((tab) => tab.id !== id);
        
        set({
          tabs: newTabs,
          activeTabId:
            state.activeTabId === id
              ? newTabs.length > 0
                ? newTabs[newTabs.length - 1].id
                : null
              : state.activeTabId,
        });
        
        // Note: We don't remove from file history when closing tabs
        // This allows users to still see recently opened files even if they're not currently open
      },

      setActiveTab: (id: string) => set({ activeTabId: id }),

      clearActiveTab: () => set({ activeTabId: null }),

      updateTabName: (id: string, newName: string) =>
        set((state) => ({
          tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, name: newName } : tab)),
        })),

      updateTabContent: (id: string, content: string) =>
        set((state) => ({
          tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, content } : tab)),
        })),

      updateTabModified: (id: string, isModified: boolean) =>
        set((state) => ({
          tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, isModified } : tab)),
        })),

      updateTabPath: (id: string, filePath: string) =>
        set((state) => ({
          tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, filePath } : tab)),
        })),

      updateTabIsNew: (id: string, isNew: boolean) =>
        set((state) => ({
          tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, isNew } : tab)),
        })),

      migrateTabId: (oldId: string, newId: string, newName: string, filePath: string) =>
        set((state) => {
          const oldTab = state.tabs.find((tab) => tab.id === oldId);
          if (!oldTab) {
            logger.warn(`Tab ${oldId} not found for migration`);
            return state;
          }

          // Create new tab with same data but new ID, name, and path
          const newTab: Tab = {
            ...oldTab,
            id: newId,
            name: newName,
            filePath,
            isModified: false, // Mark as saved
          };

          // Remove old tab and add new one
          const tabs = state.tabs
            .filter((tab) => tab.id !== oldId && tab.id !== newId) // Remove old and any existing with new ID
            .concat(newTab);

          // Update active tab ID if it was the old one
          const activeTabId = state.activeTabId === oldId ? newId : state.activeTabId;

          // Add to file history with new ID
          get().addToFileHistory(newId, oldTab.type, newName, filePath);

          logger.info('Tab ID migrated:', {
            oldId,
            newId,
            newName,
            filePath,
          });

          return {
            tabs,
            activeTabId,
          };
        }),

      reorderTabs: (sourceIndex: number, destinationIndex: number) =>
        set((state) => {
          const newTabs = Array.from(state.tabs);
          const [removed] = newTabs.splice(sourceIndex, 1);
          newTabs.splice(destinationIndex, 0, removed);
          return { tabs: newTabs };
        }),

      // Add handleAddTab to the store
      handleAddTab: (type: string, id: string | null = null, name: string | null = null, path?: string) => {
        const state = get();
        const newId = id || `tab_${Date.now()}`;
        const tabName = name || `New ${type}`;
        // Determine if this is a new file: true if no path is provided, false if path exists
        const isNew = !path;

        // Check if tab already exists
        const existingTab = state.tabs.find((tab) => tab.id === newId);
        if (existingTab) {
          // Switch to existing tab
          set({ activeTabId: newId });
          // Update file history for existing tab
          get().addToFileHistory(newId, type, tabName, path);
          logger.debug('Switched to existing tab:', {
            id: newId,
            type,
            name: tabName,
            timestamp: new Date().toISOString(),
          });
          return;
        }

        // Add the tab to the store
        set((state) => ({
          tabs: [...state.tabs, { id: newId, type, name: tabName, isModified: false, filePath: path, isNew }],
          activeTabId: newId,
        }));

        // Add to file history
        get().addToFileHistory(newId, type, tabName, path);

        logger.debug('Created new tab:', {
          id: newId,
          type,
          name: tabName,
          isNew,
          timestamp: new Date().toISOString(),
        });
      },

      // File history methods
      addToFileHistory: (id: string, type: string, name: string, path?: string) => {
        const state = get();
        const now = new Date().toISOString();
        
        // Remove existing entry if it exists (to move it to the top)
        const filteredHistory = state.fileHistory.filter(entry => entry.id !== id);
        
        // Add new entry at the beginning (most recent)
        const newEntry: FileHistoryEntry = {
          id,
          type,
          name,
          lastOpened: now,
          path
        };
        
        set({
          fileHistory: [newEntry, ...filteredHistory].slice(0, 20) // Keep only last 20 entries
        });
        
        logger.debug('Added to file history:', {
          id,
          type,
          name,
          path,
          timestamp: now
        });
      },

      removeFromFileHistory: (id: string) => {
        set((state) => ({
          fileHistory: state.fileHistory.filter(entry => entry.id !== id)
        }));
        
        logger.debug('Removed from file history:', { id });
      },

      clearFileHistory: () => {
        set({ fileHistory: [] });
        logger.debug('Cleared file history');
      },

      getRecentFiles: (limit: number = 10) => {
        const state = get();
        return state.fileHistory.slice(0, limit);
      },

      // Clear the store
      clearStore: () => {
        set({
          tabs: [],
          activeTabId: null,
          fileHistory: [],
        });
      },
    }),
    {
      name: 'tab-manager-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useTabManagerStore;
